import { MODAL_NONE } from '@components/App/Modal/constants'
import { hasOpenRadixMenu } from '@lib/escape'
import { ProjectActions } from '@octopus-do/octopus-project-core'
import { BlockActions } from '@store/blockSaga'
import {
  thunkDeleteSelectedArrowGridPoint,
  thunkDeleteSelectedArrowLabel,
  thunkDeleteSelectedArrows,
} from '@store/migrationActions/thunkActions/thunkArrowsActions'
import { thunkDeleteSelectedNodes } from '@store/migrationActions/thunkActions/thunkNodesActions'
import { thunkDeleteStickyNote } from '@store/migrationActions/thunkActions/thunkStickyNotesActions'
import { thunkCheckUsageAndDeleteSymbol } from '@store/migrationActions/thunkActions/thunkSymbolActions'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectSelectedArrowLabel } from '@store/reducers/artboard'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { selectedAttachmentsIdsSelector } from '@storeOld/reducers/canvas/selectors'
import { deleteExternalLink } from '@storeOld/reducers/externalLinks'
import { seoPanelHide } from '@storeOld/reducers/ui'
import { selectSeoPanelState } from '@storeOld/selectors'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtboardMode, usePrintRender } from '@/hooks'
import type { AppState } from '@/types'
import { allowEventTarget } from '@/utils/allowEventTarget'
import { Keys } from '../keyboard'

const useOnDeleteKeyboardActions = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { deleteSections } = useProjectLegacyActions()

  const { seoMode } = useArtboardMode()
  const { isPrinting } = usePrintRender()
  const seoNodeId = useSelector(selectSeoPanelState)
  const readOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )
  const deletionBlocked = useSelector(
    ({ artboard, ui }: AppState) =>
      artboard.isDisabled ||
      artboard.dragAndDrop.isActive ||
      ui.modalType !== MODAL_NONE ||
      ui.dialogType !== MODAL_NONE
  )

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  const selectedNodeLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedNodeLink
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // symbol selectors
  const selectedSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedSymbol
  )

  const selectedToEditSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol
  )

  // sticky note selectors
  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )

  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  // external link selectors
  const selectedExternalLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.externalLink.selectedExternalLink
  )

  // arrow selectors
  const selectedArrow = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.arrow.selectedArrowId
  )

  const selectedArrowPoint = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.arrow.selectedArrowGridPointId
  )

  const selectedArrowLabel = useSelector(selectSelectedArrowLabel)

  // section selectors
  const selectedSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionId
  )

  const selectedToEditSection = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.section.selectedSectionEditingId
  )

  // color picker
  const colorPickerIsOpen = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.colorPickerIsOpen
  )

  // tag dropdown
  const tagsDropdown = useSelector<AppState, string | null>(
    (state) => state.canvas.tags.dropdownIsShown
  )

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)
  const selectedAttachments = useSelector(selectedAttachmentsIdsSelector)

  const onDeleteNode = () => {
    dispatch(thunkDeleteSelectedNodes())
  }

  const onDeleteBlock = () => {
    dispatch(BlockActions.deleteBlock())
  }

  const onDeleteSymbol = () => {
    if (!selectedSymbol) {
      return
    }

    dispatch(thunkCheckUsageAndDeleteSymbol({ id: selectedSymbol }))
  }

  const onDeleteStickyNote = () => {
    dispatch(thunkDeleteStickyNote())
  }

  const onDeleteExternalLink = () => {
    // @todo implement action
    dispatch(deleteExternalLink())
  }

  const onDeleteArrow = () => {
    dispatch(thunkDeleteSelectedArrows())
  }

  const onDeleteArrowLabel = () => {
    dispatch(thunkDeleteSelectedArrowLabel())
  }

  const onDeleteArrowPoint = () => {
    dispatch(thunkDeleteSelectedArrowGridPoint())
  }

  const onDeleteSection = () => {
    if (selectedSection) {
      deleteSections({ ids: [selectedSection] })
    }
  }

  return (event: KeyboardEvent) => {
    if (event.code !== Keys.Delete && event.code !== Keys.Backspace) {
      return
    }

    // Typing in a field, e.g. the link popover of a page or an external link,
    // edits the field, never the selected element.
    if (!allowEventTarget(event)) {
      return
    }

    if (seoMode) {
      if (
        !seoNodeId ||
        readOnly ||
        deletionBlocked ||
        isPrinting ||
        event.defaultPrevented ||
        event.isComposing ||
        colorPickerIsOpen ||
        tagsDropdown ||
        hasOpenRadixMenu()
      ) {
        return
      }

      event.preventDefault()
      // SEO selection is the open card, not a stale sitemap multi-selection.
      // Close its panel before removing the node the form reads.
      dispatch(seoPanelHide())
      dispatch(
        commitProjectRequest(ProjectActions.nodes.delete({ ids: [seoNodeId] }))
      )

      return
    }

    if (
      selectedNode &&
      !selectedToEditNode &&
      !selectedNodeLink &&
      !colorPickerIsOpen &&
      !tagsDropdown
    ) {
      onDeleteNode()
    }

    if (selectedBlock && !selectedToEditBlock && !colorPickerIsOpen) {
      onDeleteBlock()
    }

    if (
      selectedSymbol &&
      !selectedToEditSymbol &&
      !selectedBlock &&
      !selectedToEditBlock &&
      !colorPickerIsOpen
    ) {
      onDeleteSymbol()
    }

    if (selectedStickyNote && !selectedToEditStickyNote && !colorPickerIsOpen) {
      onDeleteStickyNote()
    }

    if (selectedExternalLink && !colorPickerIsOpen) {
      onDeleteExternalLink()
    }

    if (selectedArrow && selectedArrowPoint === null) {
      if (selectedArrowLabel) {
        onDeleteArrowLabel()
      } else {
        onDeleteArrow()
      }
    }

    if (selectedArrow && selectedArrowPoint !== null) {
      onDeleteArrowPoint()
    }

    if (selectedSection && !selectedToEditSection && !colorPickerIsOpen) {
      onDeleteSection()
    }

    if (canEdit && selectedAttachments.length) {
      // @todo implement action
      dispatch(
        commitProjectRequest(
          ProjectActions.attachments.delete({ ids: selectedAttachments })
        )
      )
    }
  }
}

export default useOnDeleteKeyboardActions
